// undoable.js

export const UNDO = 'UNDO';
export const REDO = 'REDO';

const initialUndoState = {
  past: [],
  present: null,
  future: []
};

export function undoable(reducer) {
  const initialState = {
    past: [],
    present: reducer(undefined, {}), // get initial present state
    future: []
  };

  return function (state = initialState, action) {
    const { past, present, future } = state;

    switch (action.type) {
      case 'UNDO': {
        if (past.length === 0) return state; // nothing to undo
        const previousEntry = past[past.length - 1];
        const newPast = past.slice(0, past.length - 1);
        return {
          past: newPast,
          present: previousEntry.state,
          future: [present, ...future]
        };
      }
      case 'REDO': {
        if (future.length === 0) return state; // nothing to redo
        const nextState = future[0];
        const newFuture = future.slice(1);
        // Use the last past entry's description if available, otherwise use a generic description
        const lastPastDescription = past.length > 0 && past[past.length - 1]?.description 
          ? past[past.length - 1].description 
          : 'previous action';
        return {
          past: [...past, { state: present, description: lastPastDescription }],
          present: nextState,
          future: newFuture
        };
      }
      default: {
        // Delegate action handling to the wrapped reducer.
        const newPresent = reducer(present, action);
        // If no state change, don't update history.
        if (newPresent === present) {
          return state;
        }
        // Extract the description from action.meta, if any.
        const description = action.meta?.description;
        
        // Check if this is a loading state update or UI-only update (pending/fulfilled/rejected actions)
        // These don't affect workflow structure, so preserve future stack
        // Also check for elementUpdated without description (internal state syncs that shouldn't clear redo)
        const isNonStructuralUpdate = action.type && (
          action.type.includes('.pending') ||
          action.type.includes('.fulfilled') ||
          action.type.includes('.rejected') ||
          action.type.includes('loadingperform') ||
          action.type.includes('activeEnabled') ||
          action.type.includes('notification') ||
          action.type.includes('elementSelected') ||
          action.type === 'workflow/saveWorkflow/pending' ||
          action.type === 'workflow/saveWorkflow/fulfilled' ||
          action.type === 'workflow/saveWorkflow/rejected' ||
          (action.type.includes('elementUpdated') && !description) // elementUpdated without description is internal sync
        );
        
        // Track all changes with descriptions. Only exclude actions without descriptions or system-only updates
        // We now want to track elementUpdated changes (app, event, field mapping, etc.)
        if (!description) {
          // No description - likely a system update, don't track
          // But preserve future stack for non-structural updates since they don't change workflow structure
          if (isNonStructuralUpdate) {
            return {
              past,
              present: newPresent,
              future // preserve future for non-structural updates
            };
          }
          return {
            past,
            present: newPresent,
            future: [] // clear future on new action
          };
        }
        // Record the change with its description.
        return {
          past: [...past, { state: present, description }],
          present: newPresent,
          future: [] // clear future on new action
        };
      }
    }
  };
}
